// Technology detection utility
// Detects frameworks, libraries, and tools used on the webpage
class TechDetector {
    detect() {
        const detected = [];
        // Check if we're in a browser environment
        if (typeof window === 'undefined') {
            return this.formatResults([]);
        }
        // Detect JavaScript frameworks
        this.detectFrameworks(detected);
        // Detect UI libraries
        this.detectUILibraries(detected);
        // Detect build tools and bundlers
        this.detectBuildTools(detected);
        // Detect CMS platforms
        this.detectCMS(detected);
        // Detect analytics tools
        this.detectAnalytics(detected);
        // Detect testing/debugging tools
        this.detectDevTools(detected);
        // Detect CSS frameworks
        this.detectCSSFrameworks(detected);
        // Detect from meta tags
        this.detectFromMetaTags(detected);
        // Detect from window globals
        this.detectFromWindowGlobals(detected);
        return this.formatResults(detected);
    }
    detectFrameworks(detected) {
        const w = window;
        // React
        if (w.React || document.querySelector('[data-reactroot], [data-reactid]')) {
            detected.push({
                name: 'React',
                version: w.React?.version,
                type: 'framework'
            });
        }
        // Vue.js
        if (w.Vue || document.querySelector('[data-v-]')) {
            detected.push({
                name: 'Vue.js',
                version: w.Vue?.version,
                type: 'framework'
            });
        }
        // Angular
        if (w.ng || w.angular || document.querySelector('[ng-version], [ng-app]')) {
            const ngVersion = document.querySelector('[ng-version]')?.getAttribute('ng-version');
            detected.push({
                name: 'Angular',
                version: ngVersion || w.angular?.version?.full,
                type: 'framework'
            });
        }
        // Svelte
        if (document.querySelector('[class*="svelte-"]')) {
            detected.push({
                name: 'Svelte',
                type: 'framework'
            });
        }
        // Next.js
        if (w.__NEXT_DATA__ || document.querySelector('#__next')) {
            detected.push({
                name: 'Next.js',
                type: 'framework'
            });
        }
        // Nuxt.js
        if (w.__NUXT__ || document.querySelector('#__nuxt')) {
            detected.push({
                name: 'Nuxt.js',
                type: 'framework'
            });
        }
        // Gatsby
        if (w.___gatsby) {
            detected.push({
                name: 'Gatsby',
                type: 'framework'
            });
        }
        // Ember.js
        if (w.Ember) {
            detected.push({
                name: 'Ember.js',
                version: w.Ember.VERSION,
                type: 'framework'
            });
        }
        // Backbone.js
        if (w.Backbone) {
            detected.push({
                name: 'Backbone.js',
                version: w.Backbone.VERSION,
                type: 'framework'
            });
        }
    }
    detectUILibraries(detected) {
        const w = window;
        // jQuery
        if (w.jQuery || w.$?.fn?.jquery) {
            detected.push({
                name: 'jQuery',
                version: w.jQuery?.fn?.jquery || w.$?.fn?.jquery,
                type: 'library'
            });
        }
        // Lodash
        if (w._ && w._.VERSION) {
            detected.push({
                name: 'Lodash',
                version: w._.VERSION,
                type: 'library'
            });
        }
        // Underscore
        if (w._ && !w._.VERSION && w._.each) {
            detected.push({
                name: 'Underscore.js',
                type: 'library'
            });
        }
        // Moment.js
        if (w.moment) {
            detected.push({
                name: 'Moment.js',
                version: w.moment.version,
                type: 'library'
            });
        }
        // Day.js
        if (w.dayjs) {
            detected.push({
                name: 'Day.js',
                version: w.dayjs.version,
                type: 'library'
            });
        }
        // D3.js
        if (w.d3) {
            detected.push({
                name: 'D3.js',
                version: w.d3.version,
                type: 'library'
            });
        }
        // Three.js
        if (w.THREE) {
            detected.push({
                name: 'Three.js',
                version: w.THREE.REVISION,
                type: 'library'
            });
        }
        // Chart.js
        if (w.Chart) {
            detected.push({
                name: 'Chart.js',
                version: w.Chart.version,
                type: 'library'
            });
        }
        // Axios
        if (w.axios) {
            detected.push({
                name: 'Axios',
                version: w.axios.VERSION,
                type: 'library'
            });
        }
    }
    detectBuildTools(detected) {
        const w = window;
        // Webpack
        if (w.webpackJsonp || w.webpackChunk) {
            detected.push({
                name: 'Webpack',
                type: 'tool'
            });
        }
        // Vite
        if (document.querySelector('script[type="module"][src*="@vite"]')) {
            detected.push({
                name: 'Vite',
                type: 'tool'
            });
        }
        // Parcel
        if (w.__PARCEL__) {
            detected.push({
                name: 'Parcel',
                type: 'tool'
            });
        }
    }
    detectCMS(detected) {
        const w = window;
        // WordPress
        if (document.querySelector('link[href*="wp-content"], link[href*="wp-includes"]') ||
            w.wp ||
            document.querySelector('meta[name="generator"][content*="WordPress"]')) {
            const version = document.querySelector('meta[name="generator"]')?.getAttribute('content')?.match(/WordPress\s+([\d.]+)/)?.[1];
            detected.push({
                name: 'WordPress',
                version,
                type: 'cms'
            });
        }
        // Shopify
        if (w.Shopify || document.querySelector('link[href*="shopify"]')) {
            detected.push({
                name: 'Shopify',
                type: 'cms'
            });
        }
        // Wix
        if (w.wixBiSession || document.querySelector('meta[name="generator"][content*="Wix"]')) {
            detected.push({
                name: 'Wix',
                type: 'cms'
            });
        }
        // Drupal
        if (document.querySelector('meta[name="generator"][content*="Drupal"]') ||
            document.querySelector('script[src*="drupal"]')) {
            detected.push({
                name: 'Drupal',
                type: 'cms'
            });
        }
        // Joomla
        if (document.querySelector('meta[name="generator"][content*="Joomla"]')) {
            detected.push({
                name: 'Joomla',
                type: 'cms'
            });
        }
    }
    detectAnalytics(detected) {
        const w = window;
        // Google Analytics
        if (w.ga || w.gtag || w._gaq || w.google_tag_manager) {
            detected.push({
                name: 'Google Analytics',
                type: 'analytics'
            });
        }
        // Google Tag Manager
        if (w.google_tag_manager || w.dataLayer) {
            detected.push({
                name: 'Google Tag Manager',
                type: 'analytics'
            });
        }
        // Facebook Pixel
        if (w.fbq || w._fbq) {
            detected.push({
                name: 'Facebook Pixel',
                type: 'analytics'
            });
        }
        // Hotjar
        if (w.hj || w._hjSettings) {
            detected.push({
                name: 'Hotjar',
                type: 'analytics'
            });
        }
        // Mixpanel
        if (w.mixpanel) {
            detected.push({
                name: 'Mixpanel',
                type: 'analytics'
            });
        }
        // Segment
        if (w.analytics && w.analytics.track) {
            detected.push({
                name: 'Segment',
                type: 'analytics'
            });
        }
    }
    detectDevTools(detected) {
        const w = window;
        // Redux DevTools
        if (w.__REDUX_DEVTOOLS_EXTENSION__) {
            detected.push({
                name: 'Redux DevTools',
                type: 'tool'
            });
        }
        // Vue DevTools
        if (w.__VUE_DEVTOOLS_GLOBAL_HOOK__) {
            detected.push({
                name: 'Vue DevTools',
                type: 'tool'
            });
        }
        // React DevTools
        if (w.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
            detected.push({
                name: 'React DevTools',
                type: 'tool'
            });
        }
    }
    detectCSSFrameworks(detected) {
        // Bootstrap
        if (document.querySelector('link[href*="bootstrap"]') ||
            document.querySelector('[class*="col-"], [class*="container"]')) {
            detected.push({
                name: 'Bootstrap',
                type: 'library'
            });
        }
        // Tailwind CSS
        if (document.querySelector('script[src*="tailwind"]') ||
            document.documentElement.classList.toString().includes('tailwind') ||
            document.querySelector('link[href*="tailwind"]')) {
            detected.push({
                name: 'Tailwind CSS',
                type: 'library'
            });
        }
        // Material UI
        if (document.querySelector('[class*="MuiBox-"], [class*="Mui"]')) {
            detected.push({
                name: 'Material-UI',
                type: 'library'
            });
        }
        // Bulma
        if (document.querySelector('link[href*="bulma"]') ||
            document.querySelector('[class*="is-primary"], [class*="columns"]')) {
            detected.push({
                name: 'Bulma',
                type: 'library'
            });
        }
        // Foundation
        if (document.querySelector('link[href*="foundation"]')) {
            detected.push({
                name: 'Foundation',
                type: 'library'
            });
        }
    }
    detectFromMetaTags(detected) {
        const generator = document.querySelector('meta[name="generator"]');
        if (generator) {
            const content = generator.getAttribute('content');
            if (content && !detected.find(t => content.includes(t.name))) {
                detected.push({
                    name: content,
                    type: 'other'
                });
            }
        }
    }
    detectFromWindowGlobals(detected) {
        const w = window;
        // Common framework/library globals to check
        const globals = [
            { key: 'Alpine', name: 'Alpine.js', type: 'framework' },
            { key: 'htmx', name: 'htmx', type: 'library' },
            { key: 'Turbo', name: 'Turbo', type: 'library' },
            { key: 'Stimulus', name: 'Stimulus', type: 'framework' },
            { key: 'gsap', name: 'GSAP', type: 'library' },
            { key: 'anime', name: 'Anime.js', type: 'library' },
            { key: 'Swiper', name: 'Swiper', type: 'library' },
            { key: 'AOS', name: 'AOS', type: 'library' },
            { key: 'ScrollMagic', name: 'ScrollMagic', type: 'library' },
            { key: 'lottie', name: 'Lottie', type: 'library' },
            { key: 'Prism', name: 'Prism', type: 'library' },
            { key: 'hljs', name: 'Highlight.js', type: 'library' },
            { key: 'Quill', name: 'Quill', type: 'library' },
            { key: 'tinymce', name: 'TinyMCE', type: 'library' }
        ];
        globals.forEach(({ key, name, type }) => {
            if (w[key] && !detected.find(t => t.name === name)) {
                detected.push({
                    name,
                    version: w[key].version || w[key].VERSION,
                    type
                });
            }
        });
    }
    formatResults(technologies) {
        // Remove duplicates
        const unique = technologies.filter((tech, index, self) => index === self.findIndex((t) => t.name === tech.name));
        return {
            frameworks: unique.filter(t => t.type === 'framework'),
            libraries: unique.filter(t => t.type === 'library'),
            tools: unique.filter(t => t.type === 'tool'),
            cms: unique.filter(t => t.type === 'cms'),
            analytics: unique.filter(t => t.type === 'analytics'),
            other: unique.filter(t => t.type === 'other'),
            allTechnologies: unique
        };
    }
}
export default TechDetector;
//# sourceMappingURL=tech-detector.js.map